import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, ExternalLink } from 'lucide-react';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogTitle } from '../../ui/dialog';
import { AuthImg } from '../AuthImg';

/** Pops the invoice PDF up as page images (works on phones) with an "Open PDF" escape hatch. */
export function InvoicePreviewDialog({ api, invoiceId, open, onClose, title = 'Invoice preview' }) {
  const [pages, setPages] = useState(null);
  const [busy, setBusy] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!open || !invoiceId) return;
    setPages(null); setNonce(n => n + 1);
    api.get(`/portal/invoices2/${invoiceId}/pages`).then(r => setPages(r.data.pages || 1)).catch(e => { toast.error(e.response?.data?.detail || 'Could not render the preview'); onClose(); });
  }, [api, invoiceId, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const openPdf = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/portal/invoices2/${invoiceId}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const w = window.open(url, '_blank');
      if (!w) { const a = document.createElement('a'); a.href = url; a.download = 'invoice.pdf'; document.body.appendChild(a); a.click(); a.remove(); }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Could not open the PDF'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent aria-describedby={undefined} className="bg-[#1E2530] border-white/10 text-white p-0 w-[96vw] max-w-[430px] h-[92vh] flex flex-col overflow-hidden" data-testid="invoice-preview-dialog">
        <div className="px-4 py-3 border-b border-white/10 pr-12"><DialogTitle className="text-base truncate">{title}</DialogTitle></div>
        <div className="flex-1 min-h-0 overflow-auto bg-[#0F141B] p-2 space-y-2" data-testid="invoice-preview-pages">
          {pages === null ? <div className="h-full flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
            : Array.from({ length: pages }, (_, i) => <AuthImg key={`${nonce}-${i}`} src={`/portal/invoices2/${invoiceId}/pages/${i}.png?width=900&v=${nonce}`} alt={`Page ${i + 1}`} className="w-full bg-white shadow-2xl" />)}
        </div>
        <div className="p-3 border-t border-white/10 grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={onClose} className="h-12 border-white/20 text-white bg-white/5" data-testid="invoice-preview-close">Close</Button>
          <Button onClick={openPdf} disabled={busy} className="h-12 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold" data-testid="invoice-preview-open-pdf">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ExternalLink className="w-4 h-4 mr-2" /> Open PDF</>}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
